import {useQuery} from '@tanstack/react-query';
import {useCallback, useEffect, useRef, useState} from 'react';

export interface TaskPollingStatus<TResult = unknown> {
  status: string;
  result?: TResult | null;
}

export interface UseTaskPollingOptions<TResult, TArgs extends unknown[] = []> {
  queryKeyPrefix: unknown[];
  startTask: (...args: TArgs) => Promise<{task_id: string}>;
  getStatus: (taskId: string) => Promise<TaskPollingStatus<TResult>>;
  refetchInterval?: number;
  enabled?: boolean;
  onCompleted?: (result: TResult) => void;
  onFailed?: () => void;
}

export function useTaskPolling<TResult, TArgs extends unknown[] = []>({
  queryKeyPrefix,
  startTask,
  getStatus,
  refetchInterval = 1000,
  enabled = true,
  onCompleted,
  onFailed,
}: UseTaskPollingOptions<TResult, TArgs>): {
  start: (...args: TArgs) => Promise<void>;
  isPolling: boolean;
} {
  const onCompletedRef = useRef(onCompleted);
  const onFailedRef = useRef(onFailed);
  onCompletedRef.current = onCompleted;
  onFailedRef.current = onFailed;

  const [taskId, setTaskId] = useState<string | undefined>(undefined);

  const start = useCallback(
    async (...args: TArgs) => {
      const {task_id} = await startTask(...args);
      setTaskId(task_id);
    },
    [startTask]
  );

  const {data: status, isError} = useQuery({
    queryKey: [...queryKeyPrefix, taskId],
    queryFn: () => getStatus(taskId!),
    enabled: Boolean(enabled && taskId),
    refetchInterval,
  });

  useEffect(() => {
    if (!status) return;
    if (status.status === 'completed') {
      if (status.result != null) {
        onCompletedRef.current?.(status.result as TResult);
      }
      setTaskId(undefined);
    } else if (status.status === 'failed') {
      onFailedRef.current?.();
      setTaskId(undefined);
    }
  }, [status]);

  useEffect(() => {
    if (isError && taskId !== undefined) {
      onFailedRef.current?.();
      setTaskId(undefined);
    }
  }, [isError, taskId]);

  return {start, isPolling: Boolean(taskId)};
}
